/**
 * Shared MyTrace / LeafLink discount money helpers.
 *
 * Rule: round the discount to cents first, then derive order total, then due =
 * total − payments. Never round(subtotal − raw% − payments) as a separate
 * expression from total — those two rounded results can disagree by a cent.
 *
 * Cart create must NOT use computeOrderBalance: its composition is
 * orderTotal − discount where orderTotal already includes tax + shipping.
 * Use computeDiscountDollars only there.
 */

export function roundMoney(n) {
  return Number(parseFloat(Number(n)).toFixed(2));
}

/**
 * Cents-rounded dollar discount for a MyTrace "%" or "$" discount.
 * Same numeric result as historical getLeaflinkDiscountPayload().discount.
 */
export function computeDiscountDollars({ type = "%", value, subtotal = 0 }) {
  const numericValue = Number(value) || 0;

  if (numericValue === 0) {
    return 0;
  }

  const dollarAmount =
    type === "%" ? (numericValue / 100) * Number(subtotal) : numericValue;

  return roundMoney(dollarAmount);
}

/**
 * Compute order balance.total / due from line subtotal + discount + shipping
 * + applied payments. due is always roundMoney(total − paymentsApplied).
 *
 * Do not use for SalesOrderCart create (tax is inside orderTotal there).
 */
export function computeOrderBalance({
  lineSubtotal,
  discountType = "%",
  discountValue = 0,
  shipping = 0,
  paymentsApplied = 0,
}) {
  const discountTotal = computeDiscountDollars({
    type: discountType,
    value: discountValue,
    subtotal: lineSubtotal,
  });
  // Use ?? not || so NaN paymentsApplied is preserved for finite guards
  // (NaN is falsy; `NaN || 0` would silently become 0).
  const total = roundMoney(
    Number(lineSubtotal) - discountTotal + Number(shipping ?? 0),
  );
  const due = roundMoney(total - Number(paymentsApplied ?? 0));
  return { total, due, discountTotal };
}

/**
 * Converts a MyTrace order discount (which may be a "%" or "$" type) into
 * the dollar-only payload LeafLink should receive, so LeafLink's inconsistent
 * per-cent rounding on "%" discounts never causes a total mismatch.
 */
export function getLeaflinkDiscountPayload({ type = "%", value, subtotal = 0 }) {
  return {
    discount: computeDiscountDollars({ type, value, subtotal }),
    discount_type: "$",
  };
}
